import { isServiceWorkerReady, waitForServiceWorker } from '../lib/serviceWorker';

const MAX_CONCURRENT_LOADS = 6;
const LOAD_TIMEOUT_MS = 10000;
const REQUEST_DELAY_MS = 30;
const FAILED_URL_CACHE_KEY = 'invfu_failed_image_urls';
const FAILED_URL_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const LRU_MAX_ENTRIES = 500;

interface FailedUrlCache {
  urls: Record<string, number>;
  lastCleanup: number;
}

interface QueueItem {
  url: string;
  resolve: (result: ImageLoadResult) => void;
}

interface ImageLoadResult {
  url: string;
  success: boolean;
  dataUrl?: string;
}

class EnterpriseImageLoader {
  private queue: QueueItem[] = [];
  private activeLoads = 0;
  private memoryCache = new Map<string, ImageLoadResult>();
  private inProgress = new Map<string, Promise<ImageLoadResult>>();
  private failedUrlCache: FailedUrlCache;
  private swInitialized = false;

  constructor() {
    this.failedUrlCache = this.loadFailedUrlCache();
    this.cleanupExpiredFailedUrls();
    this.initServiceWorker();
  }

  private async initServiceWorker(): Promise<void> {
    if (this.swInitialized) return;
    this.swInitialized = true;
    await waitForServiceWorker();
  }

  private loadFailedUrlCache(): FailedUrlCache {
    try {
      const stored = localStorage.getItem(FAILED_URL_CACHE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
    }
    return { urls: {}, lastCleanup: Date.now() };
  }

  private saveFailedUrlCache(): void {
    try {
      localStorage.setItem(FAILED_URL_CACHE_KEY, JSON.stringify(this.failedUrlCache));
    } catch {
    }
  }

  private cleanupExpiredFailedUrls(): void {
    const now = Date.now();
    if (now - this.failedUrlCache.lastCleanup < 60 * 60 * 1000) {
      return;
    }

    const expiredBefore = now - FAILED_URL_CACHE_TTL_MS;
    const urls = this.failedUrlCache.urls;

    for (const url of Object.keys(urls)) {
      if (urls[url] < expiredBefore) {
        delete urls[url];
      }
    }

    this.failedUrlCache.lastCleanup = now;
    this.saveFailedUrlCache();
  }

  private markUrlAsFailed(url: string): void {
    this.failedUrlCache.urls[url] = Date.now();
    this.saveFailedUrlCache();
  }

  private isUrlKnownFailed(url: string): boolean {
    const failedAt = this.failedUrlCache.urls[url];
    if (!failedAt) return false;

    if (Date.now() - failedAt > FAILED_URL_CACHE_TTL_MS) {
      delete this.failedUrlCache.urls[url];
      return false;
    }

    return true;
  }

  async loadImage(url: string): Promise<ImageLoadResult> {
    if (!url) {
      return { url: '', success: false };
    }

    if (this.isUrlKnownFailed(url)) {
      return { url, success: false };
    }

    const cached = this.memoryCache.get(url);
    if (cached) {
      this.memoryCache.delete(url);
      this.memoryCache.set(url, cached);
      return cached;
    }

    const existing = this.inProgress.get(url);
    if (existing) {
      return existing;
    }

    const promise = new Promise<ImageLoadResult>((resolve) => {
      this.queue.push({ url, resolve });
      this.processQueue();
    });

    this.inProgress.set(url, promise);
    return promise;
  }

  private async processQueue(): Promise<void> {
    if (this.activeLoads >= MAX_CONCURRENT_LOADS || this.queue.length === 0) {
      return;
    }

    const item = this.queue.shift();
    if (!item) return;

    this.activeLoads++;

    try {
      const result = await this.loadImageWithStrategy(item.url);
      this.memoryCache.set(item.url, result);
      this.evictLru();

      if (!result.success) {
        this.markUrlAsFailed(item.url);
      }

      item.resolve(result);
    } catch {
      const failResult: ImageLoadResult = { url: item.url, success: false };
      this.memoryCache.set(item.url, failResult);
      this.markUrlAsFailed(item.url);
      item.resolve(failResult);
    } finally {
      this.activeLoads--;
      this.inProgress.delete(item.url);

      if (this.queue.length > 0) {
        setTimeout(() => this.processQueue(), REQUEST_DELAY_MS);
      }
    }
  }

  private async loadImageWithStrategy(url: string): Promise<ImageLoadResult> {
    if (isServiceWorkerReady()) {
      return this.loadImageWithFetch(url);
    }
    return this.loadImageWithFetch(url);
  }

  private async loadImageWithFetch(url: string): Promise<ImageLoadResult> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const isProxied = response.headers.get('X-Proxied') === 'true';
      if (isProxied) {
        const originalStatus = parseInt(response.headers.get('X-Original-Status') || '0', 10);
        if (originalStatus >= 400) {
          return { url, success: false };
        }
      }

      if (!response.ok && !isProxied) {
        return { url, success: false };
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        return { url, success: false };
      }

      if (!blob.type.startsWith('image/')) {
        return { url, success: false };
      }

      const dataUrl = await this.blobToDataUrl(blob);
      return { url, success: true, dataUrl };

    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof DOMException && error.name === 'AbortError') {
        return { url, success: false };
      }

      return { url, success: false };
    }
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert blob'));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private evictLru(): void {
    while (this.memoryCache.size > LRU_MAX_ENTRIES) {
      const oldest = this.memoryCache.keys().next().value;
      if (oldest !== undefined) this.memoryCache.delete(oldest);
      else break;
    }
  }

  clearMemoryCache(): void {
    this.memoryCache.clear();
    this.inProgress.clear();
  }

  clearFailedUrlCache(): void {
    this.failedUrlCache = { urls: {}, lastCleanup: Date.now() };
    this.saveFailedUrlCache();
  }

  clearAllCaches(): void {
    this.clearMemoryCache();
    this.clearFailedUrlCache();
  }

  getFailedUrlCount(): number {
    return Object.keys(this.failedUrlCache.urls).length;
  }

  preloadImages(urls: string[]): Promise<ImageLoadResult[]> {
    const uniqueUrls = [...new Set(urls.filter(Boolean))];
    return Promise.all(uniqueUrls.map((url) => this.loadImage(url)));
  }
}

export const imageLoader = new EnterpriseImageLoader();

export async function preloadPickListImages(
  imageUrls: string[],
  onProgress?: (loaded: number, total: number) => void
): Promise<Map<string, string | null>> {
  const uniqueUrls = [...new Set(imageUrls.filter(Boolean))];
  const total = uniqueUrls.length;
  const resultMap = new Map<string, string | null>();

  if (total === 0) {
    return resultMap;
  }

  let loaded = 0;

  const loadPromises = uniqueUrls.map(async (url) => {
    const result = await imageLoader.loadImage(url);
    loaded++;
    onProgress?.(loaded, total);
    return result;
  });

  const results = await Promise.all(loadPromises);

  for (const result of results) {
    resultMap.set(result.url, result.success ? (result.dataUrl || result.url) : null);
  }

  return resultMap;
}

export function clearImageCaches(): void {
  imageLoader.clearAllCaches();
}

export function getFailedImageCount(): number {
  return imageLoader.getFailedUrlCount();
}
